import { AsyncPipe } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { AnnualStatementStatus, SendOption } from '@models/annual-statements/annual-statement-enums';
import { Contact } from "@models/common/contact";
import { ESignBody } from "@models/documents/sign-documents";
import { Signature } from "@models/documents/signature";
import { EmailSendDocumentRequest } from "@models/email-templates/email-bulk-send-request";
import { EmailMultipleAddress } from "@models/email-templates/email-multiple-address";
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { EmailTemplateKey, EmailTemplatesHelper } from "@models/email-templates/email-templates-helper";
import { UserRecord } from '@models/organisations/user-record';
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { FilesResponse } from "@models/ui-models/files";
import { SelectOption } from "@models/ui-models/select-options";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { EmailPreviewComponent } from "@shared/components/email-preview/email-preview.component";
import { MultipleInputEmailComponent } from '@controls/multiple-input-email/multiple-input-email.component';
import { RadioComponent } from "@controls/radio/radio.component";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { ESignApiService } from "@shared/services/e-sign-api.service";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { UsersApiService } from "@shared/services/users-api.service";
import { clearFormArray } from "@shared/utils/clear-form-array";
import { getAnnualStatementsPath } from "@shared/utils/route-path-utils";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  tap
} from "rxjs";
import {
  contactWithEmailRequiredMessage
} from "../../../../../../forms/controls/contacts-select/constants/contact-validation-messages";
import { ContactsSelectComponent } from "../../../../../../forms/controls/contacts-select/contacts-select.component";

export const sendOptionEmailTemplateCodes: Partial<Record<SendOption, EmailTemplateKey>> = {
  [SendOption.Email]: 'as-email',
  [SendOption.Sign]: 'as-esign',
};

@Component({
  selector: 'app-annual-view',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgxSkeletonLoaderModule,
    AsyncPipe,
    CommonModalFormComponent,
    RadioComponent,
    DividerComponent,
    FileLabelComponent,
    MultipleInputEmailComponent,
    EmailPreviewComponent,
    ContactsSelectComponent,
  ],
  providers: [EmailsApiService],
  templateUrl: 'annual-view-and-send-modal.component.html',
  styleUrl: 'annual-view-and-send-modal.component.scss'
})
export class AnnualViewAndSendModalComponent implements OnInit {
  private filesApiService = inject(FilesApiService);
  private annualStatementsApiService = inject(AnnualStatementsApiService);
  private emailTemplatesService = inject(EmailsApiService);
  private eSignService = inject(ESignApiService);
  private router = inject(Router);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private documentCommonService = inject(DocumentCommonService);
  private contactsApiService = inject(ContactsApiService);
  private usersService = inject(UsersApiService);

  @Input() documentId = '';

  @Output() confirm = new EventEmitter();

  SendOption = SendOption;

  readonly AnnualStatementStatus = AnnualStatementStatus;
  readonly defaultCoverLetterTemplateName = 'AS Cover Letter Default Template';
  readonly sendByOptions: SelectOption[] = [
    { label: 'eSign', value: SendOption.Sign },
    { label: 'Email', value: SendOption.Email },
  ];
  readonly contactWithEmailRequiredMessage = contactWithEmailRequiredMessage;

  annual: AnnualStatement | undefined;
  emailBody: Partial<ESignBody> | undefined;
  selectedEmailTemplateName = '';
  loading = signal(false);
  isAnnualLoading = signal(false);
  isSendLoading = signal(false);
  emailList: EmailMultipleAddress[] = [];

  userList: UserRecord[] = [];

  isContactsLoading = signal(false);
  companyDirectors: Relationship[] = [];
  directorsContacts: Record<string, Contact[]> = {};
  directorsSelectedContacts: Record<string, Contact | null> = {};

  documentId$ = new ReplaySubject<string>(1);
  refreshList$ = new BehaviorSubject(false);
  documentsList$: Observable<FilesResponse> = combineLatest([this.refreshList$, this.documentId$])
    .pipe(
      tap(() => {
        this.loading.set(true);
        this.cdr.detectChanges();
      }),
      switchMap(([_, documentId]) => this.filesApiService.getFiles(documentId)),
      tap(() => this.loading.set(false)),
      shareReplay(1),
    );

  form = new FormGroup({
    sendBy: new FormControl<SendOption>(SendOption.Sign, { nonNullable: true }),
  });

  areContactsValid = false;
  contactsFormArray = new FormArray<FormControl<string | null>>([]);

  ngOnInit(): void {
    this.documentId$.next(this.documentId);
    this.loadAnnualStatement();
    this.getUserList();

    this.form.controls.sendBy.valueChanges
      .pipe(
        startWith(this.form.controls.sendBy.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((sendBy) => {
        this.selectedEmailTemplateName = EmailTemplatesHelper.TemplateHeadersRecord[sendOptionEmailTemplateCodes[sendBy]!];
      });
  }

  getUserList() {
    this.usersService.getAssignableUsersCached().pipe(
      tap((users) => {
        this.userList = users;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  loadCompanyContacts(companyId: string): void {
    this.isContactsLoading.set(true);

    this.companyDirectors.forEach((director, index) => {
      this.directorsContacts[director.individualId ?? director.key] = [];

      if (director.contact && !this.contactsFormArray.controls[index].value) {
        // fill directorsSelectedContacts and related controls with primary contacts

        this.directorsSelectedContacts[director.individualId ?? director.key] = director.contact ?? null;
        this.contactsFormArray.controls[index].setValue(director.contactId ?? null);
      }
    });

    this.contactsApiService.getContacts(companyId, [RelationshipType.Director])
      .subscribe({
        next: (directorsContacts) => {
          directorsContacts.forEach((contact) => {
            const individualId = contact.individualId;

            if (!individualId) return;

            if (individualId && this.directorsContacts[individualId]) {
              this.directorsContacts[individualId].push(contact);
            } else {
              this.directorsContacts[individualId] = [contact];
            }
          });

          // fill empty directorsSelectedContacts and related controls with first related contact
          this.companyDirectors.forEach((director, index) => {
            if (director.individualId && (!this.directorsSelectedContacts[director.individualId] || !this.contactsFormArray.controls[index].value)) {
              const firstRelatedContact: Contact | null = this.directorsContacts[director.individualId]?.[0] ?? null;

              this.directorsSelectedContacts[director.individualId ?? director.key] = firstRelatedContact;
              this.contactsFormArray.controls[index].setValue(firstRelatedContact?.contactId ?? null);
            }
          });

          this.isContactsLoading.set(false);
          this.checkContactsValid();
        }
      });
  }

  loadAnnualStatement(): void {
    this.isAnnualLoading.set(true);
    this.annualStatementsApiService.getAnnualById(this.documentId)
      .subscribe({
        next: (annualStatement) => {
          if (annualStatement) {
            this.annual = annualStatement;

            this.fillContactControlsWithDirectors();
            this.loadCompanyContacts(annualStatement.company.entityId);
          }

          this.isAnnualLoading.set(false);
        },
        error: (error) => {
          console.error('[loadAnnualStatement] error: ', error);
          return of(null);
        }
      });
  }

  send(): void {
    if (this.isNotValidForSend) return;

    if (this.form.controls.sendBy.value === SendOption.Sign) {
      this.sendToESign();
    } else if (this.form.controls.sendBy.value === SendOption.Email) {
      this.sendEmail();
    }
  }

  sendToESign(): void {
    const documentId = this.annual?.documentId;
    let eSignBody: ESignBody | undefined;

    if (this.emailBody?.payloadModel && this.emailBody?.emailTemplate) {
      eSignBody = {
        signatures: this.getSignatures(),
        carbonCopyList: [],
        approver: null,
        payloadModel: this.emailBody.payloadModel,
        emailTemplate: this.emailBody.emailTemplate,
        includeAttachedFiles: false,
      };
    }

    if (!documentId || !eSignBody) {
      return;
    }

    this.isSendLoading.set(true);
    this.eSignService.sendToESign(documentId, eSignBody)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
          next: (documentSigning) => {
            if (documentSigning) {
              this.toastr.success('Documents have been successfully sent for eSign', 'Success');
              this.confirm.emit(true);
            }
            this.isSendLoading.set(false);
          },
          error: (error) => {
            console.error('[sendToESign] error: ', error);
            this.toastr.error('Send for eSign error', 'Error');
            this.isSendLoading.set(false);
          }
        }
      );
  }

  sendEmail(): void {
    let emailSendRequest: EmailSendDocumentRequest | undefined;
    if (this.emailBody?.payloadModel && this.emailBody?.emailTemplate) {
      emailSendRequest = {
        recipientsList: this.emailList.map(x => x.email) ?? [],
        payloadModel: this.emailBody.payloadModel as EmailPayloadBase,
        template: this.emailBody.emailTemplate,
        documentsToAttachedId: [],
        includeAttachedFiles: false
      };
    }

    if (!emailSendRequest) {
      return;
    }

    this.isSendLoading.set(true);
    this.emailTemplatesService.sendDocument(emailSendRequest, this.documentId)
      .subscribe({
        next: () => {
          this.toastr.success('Email has been successfully sent', 'Success');
          this.isSendLoading.set(false);
          this.confirm.emit(true);
        },
        error: (error) => {
          console.error('[sendBulkEmailTemplate] error: ', error);
          this.toastr.error('Send email error', 'Error');
          this.isSendLoading.set(false);
          return EMPTY;
        }
      });
  }

  changeEmail(emailBody: Partial<ESignBody>): void {
    this.emailBody = emailBody;
  }

  customize(): void {
    if (this.documentId) {
      void this.router.navigate(getAnnualStatementsPath(this.documentId));
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }

  downloadFile(fileName: string, subFolder: string): void {
    this.documentCommonService.downloadFile(this.annual!.documentId, fileName, subFolder);
  }

  fillContactControlsWithDirectors(): void {
    clearFormArray(this.contactsFormArray);

    this.companyDirectors = this.annual?.company.officers.filter((officer) => officer.type == RelationshipType.Director) ?? [];
    this.companyDirectors?.forEach((director) => {
      const contactControl = new FormControl<string | null>(null, [Validators.required]);

      if (director.contactId || director.contact?.contactId) {
        contactControl.setValue(director.contactId ?? director.contact?.contactId ?? null);
        this.onDirectorContactSelected(director.individualId, director.contact);
      }

      this.contactsFormArray.push(contactControl);
    });
  }

  onContactCreated(newDirectorContact: Contact): void {
    if (newDirectorContact.individualId && this.directorsContacts[newDirectorContact.individualId]) {
      this.directorsContacts[newDirectorContact.individualId].push(newDirectorContact);
      this.directorsSelectedContacts[newDirectorContact.individualId] = newDirectorContact;

      const relatedDirectorIndex = this.companyDirectors.findIndex((director) => director.individualId && director.individualId == newDirectorContact.individualId);
      if (relatedDirectorIndex >= 0) {
        this.contactsFormArray.controls[relatedDirectorIndex].setValue(newDirectorContact.contactId);
      }
    }
  }

  onDirectorContactSelected(directorIndividualId: string | null, contact: Contact | null | undefined): void {
    const individualId = directorIndividualId ?? contact?.individualId ?? null;

    if (individualId) {
      this.directorsSelectedContacts[individualId] = contact ?? null;
    }

    this.checkContactsValid();
  }

  checkContactsValid(): void {
    this.areContactsValid = Object.values(this.directorsSelectedContacts)
      .every((contact) => contact && contact.email.trim());
  }

  getSignatures(): Signature[] {
    return this.companyDirectors.map((director) => {
      const individualId = director.individualId!;
      const selectedContact = this.directorsSelectedContacts[individualId]!;

      return new Signature({
        signatory: director.individualDataOverride!,
        email: selectedContact.email,
        phone: selectedContact.phone,
      });
    })
  }

  get partnerManagerName() {
    return this.userList.find(user => user.id === this.annual?.company.partnerManagerId)?.fullName ?? '';
  }

  get isNotValidForSend(): boolean {
    if (this.form.controls.sendBy.value === SendOption.Sign) {
      return this.contactsFormArray.invalid || !this.areContactsValid;
    } else if (this.form.controls.sendBy.value === SendOption.Email) {
      return !this.emailList.length;
    }

    return false;
  }
}
